/* eslint-disable no-mixed-spaces-and-tabs */
import { Box, Grid } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useColumnSelector } from "../../../../../../export/attendance-reports";
import {
  getCompanyUsers,
  getLocations,
  getRolesForAdmin,
  resetCreateCompanyUser,
  resetUpdateCompanyUser,
  setCompanyUserDialog
} from "../../../../../../export/gat-admin";
import { PageHeader } from "../../../../../../export/page-header";
import AppliedFilters from "../../../../filters/applied-filters";
import { FilterContext } from "../../../../filters/filter-context";
import useSupervisorColumns
  from "../../../../gat-admin/admin-config-companies-settings/supervisors/ui/controllers/use-supervisor-columns";
import useGatAdminConfigData from "../../../../gat-admin/controllers/use-gat-admin-config-data";
import useColumnDivider from "../../../../workers/controllers/use-column-divider";
import { calculateMaxWidth } from "../../../../workers/utils";
import { HR_MANAGER, SHIFT_SUPERVISOR } from "../constant";
import CompanyUserDialog from "./company-user-dialog";
import useSupervisorsData from "./controllers/use-supervisors-data";
import useUsersFilters from "./controllers/use-users-filters";
import SupervisorHeader from "./users-header";
import SupervisorTable from "./users-table";

const defaultCompanyUserInfo = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  isActive: true,
  roleId: 5,
  workCenterIds: "",
  phoneNumber: "",
  companyId: 0,
};

interface SupervisorsProps {
  companyData: any;
  setWorkCenterData: any;
}

const Supervisors: React.FC<SupervisorsProps> = ({
  companyData,
  setWorkCenterData,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    createCompanyUserStatus,
    createCompanyUserErrorMessage,
    updateCompanyUserStatus,
    updateCompanyUserErrorMessage,
  } = useSupervisorsData();

  const { getLocationsStatus } = useGatAdminConfigData();

  // Registers Location / Work Center / User Roles / Status filters into the
  // FilterProvider (see hook). Kept as a hook so context + effect stay together.
  useUsersFilters();

  const [companyUserData, setCompanyUserData] = useState<any>({});
  const [creatingNewCompanyUser, setCreatingNewCompanyUser] = useState(false);
  const [newCompanyUserInfo, setNewCompanyUserInfo] = useState(
    defaultCompanyUserInfo
  );
  const [searchText, setSearchText] = useState("");
  // The dialog resets newCompanyUserInfo when it closes on success, which
  // happens before the snackbar renders, so keep the name separately.
  const [createdUserName, setCreatedUserName] = useState("");

  const companyId = useSelector(
    (state: any) => state?.user?.userData?.companyId
  );
  const isDrawerOpen = useSelector(
    (state: { navigationDrawer: { isDrawerOpen: boolean } }) =>
      state.navigationDrawer.isDrawerOpen
  );

  const [
    showCreateNewCompanyUserSuccessMessage,
    setShowCreateNewCompanyUserSuccessMessage,
  ] = useState(false);

  useEffect(() => {
    if (creatingNewCompanyUser) {
      dispatch(setCompanyUserDialog(true));
    }
  }, [dispatch, creatingNewCompanyUser]);

  useEffect(() => {
    if (dispatch
      && (companyId || companyData.id)
      && getLocationsStatus === "idle"
    ) {
      dispatch(getLocations(companyId ?? companyData.id));
    }
  }, [dispatch, companyId, companyData.id, getLocationsStatus]);

  useEffect(() => {
    if (dispatch && getLocationsStatus === "fulfilled" && (companyId || companyData.id)) {
      dispatch(getCompanyUsers(companyId ?? companyData.id));
      dispatch(getRolesForAdmin());
    }
  }, [dispatch, companyId, companyData.id, getLocationsStatus]);

  useEffect(() => {
    if (createCompanyUserStatus === "fulfilled") {
      setCreatedUserName(
        `${newCompanyUserInfo.firstName.trim()} ${newCompanyUserInfo.lastName.trim()}`.trim()
      );
      setShowCreateNewCompanyUserSuccessMessage(true);
      dispatch(resetCreateCompanyUser());
    }
  }, [createCompanyUserStatus, dispatch, newCompanyUserInfo]);

  const filterContext = useContext(FilterContext);
  if (!filterContext) {
    throw new Error("FilterContext must be used within a FilterProvider");
  }

  const { appliedFilters, filterAccordionHeight } = filterContext;

  const columns: any = useSupervisorColumns(searchText);
  const fixedColumns = [
    "email",
    "userRole",
    "workcenters",
    "userWorkCenters",
    "managerName",
    "status",
  ];

  const { defaultColumns, restColumns } = useColumnDivider(
    columns,
    fixedColumns
  );

  const [{ selectedColumns, pickerColumns }, { handleSelectColumns }]: any =
    useColumnSelector(defaultColumns, restColumns);

  const finalPickerColumn = pickerColumns.filter((pickerColumn: any) =>
    fixedColumns.includes(pickerColumn.field)
  );
  const shiftSupervisor =  companyUserData?.roles?.find((role : any)  => role.name === SHIFT_SUPERVISOR)?.id ?? -1
  const trainingManagerId = companyUserData?.roles?.find((role: any) => role.name === "TrainingManager")?.id
  const trainingCoordinatorId = companyUserData?.roles?.find((role: any) => role.name === "TrainingCoordinator")?.id
  const departmentManagerId = companyUserData?.roles?.find((role: any) => role.name === "DepartmentManager")?.id


  return (
    <>
      <Box sx={{ paddingTop: "1px" }}>
        <PageHeader title={t("users_title")} subtitle="" />
      </Box>
      <Divider />
      <SupervisorHeader
        openDialog={() => setCreatingNewCompanyUser(true)}
        setSearch={setSearchText}
        searchText={searchText}
        columns={finalPickerColumn}
        handleSelectColumns={handleSelectColumns}
      />
      <Divider />
      <Grid container wrap="nowrap" spacing={0}>
        <Grid item xs lg md sm>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: "15px",
                minHeight: `${filterAccordionHeight}px`,
                maxWidth: "100%",
                flex: 1,
              }}
            >
              <AppliedFilters />
              <SupervisorTable
                setCompanyUserData={(value: any) => {
                  const newCompanyUserData = { ...value };
                  delete newCompanyUserData.changeCompanyUserData;
                  setCompanyUserData(newCompanyUserData);
                  dispatch(setCompanyUserDialog(true));
                }}
                setWorkCenterData={setWorkCenterData}
                searchText={searchText}
                appliedFilters={appliedFilters}
                columns={columns.filter((column: any) =>
                  selectedColumns.includes(column.field)
                )}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <CompanyUserDialog
        creatingNewCompanyUser={creatingNewCompanyUser}
        companyUserData={
          creatingNewCompanyUser
            ? { ...newCompanyUserInfo, companyId: companyId }
            : {
                ...companyUserData,
                companyId: companyId,
                roleId:
                  companyUserData.roleId === undefined
                    ? Object.prototype.hasOwnProperty.call(
                        companyUserData,
                        "roles"
                      )
											? companyUserData?.roles?.find((role : any)  => role.name === HR_MANAGER)?.id  ?? trainingManagerId ?? trainingCoordinatorId ?? departmentManagerId ?? (shiftSupervisor === -1 ? companyUserData?.roles?.[0]?.id : shiftSupervisor) :  (shiftSupervisor === -1 ? companyUserData?.roles?.[0]?.id : shiftSupervisor)
                    : companyUserData?.roleId,
                workCenterIds:
                  companyUserData.workCenterIds === undefined ||
                  companyUserData.workCenterIds === ""
                    ? companyUserData.userWorkCenters
                      ? companyUserData.userWorkCenters
                          .map((wc: any) => wc.id)
                          .toString()
                      : ""
                    : companyUserData.workCenterIds,
              }
        }
        setCompanyUserData={(newValue: any) =>
          creatingNewCompanyUser
            ? setNewCompanyUserInfo(newValue)
            : setCompanyUserData(newValue)
        }
        onClose={() => {
          dispatch(setCompanyUserDialog(false));
          setNewCompanyUserInfo(defaultCompanyUserInfo);
          setCompanyUserData({});

          // To handle UI glitch of showing Create button on cancel
          setTimeout(() => {
            setCreatingNewCompanyUser(false);
          }, 500);
        }}
      />

      <Snackbar
        open={createCompanyUserStatus === "error"}
        autoHideDuration={3000}
        onClose={() => {
          dispatch(resetCreateCompanyUser());
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} variant="filled" severity="error">
          {createCompanyUserErrorMessage ?? t("common_error-try-again")}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={showCreateNewCompanyUserSuccessMessage}
        autoHideDuration={3000}
        onClose={() => {
          setCreatingNewCompanyUser(false);
          setNewCompanyUserInfo(defaultCompanyUserInfo);
          setShowCreateNewCompanyUserSuccessMessage(false);
          dispatch(resetCreateCompanyUser());
          dispatch(setCompanyUserDialog(false));
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">
          {t("users_supervisor-added", { name: createdUserName })}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={updateCompanyUserStatus === "error"}
        autoHideDuration={3000}
        onClose={() => {
          dispatch(resetUpdateCompanyUser());
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} variant="filled" severity="error">
          {updateCompanyUserErrorMessage ?? t("common_error-try-again")}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={updateCompanyUserStatus === "fulfilled"}
        autoHideDuration={3000}
        onClose={() => {
          setCompanyUserData({});
          dispatch(resetUpdateCompanyUser());
          dispatch(setCompanyUserDialog(false));
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">
          {t("users_supervisor-updated")}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default Supervisors;
